import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { scrollToHash } from "@/lib/scrollTo";
import { useLanguage } from "@/lib/language";

const EASE = [0.16, 1, 0.3, 1];

export const Intro = () => {
  const { t } = useLanguage();
  return (
    <section
      id="o-rezydencji"
      data-testid="intro-section"
      className="bg-cream py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-3">
          <motion.p
            initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="text-[11px] uppercase tracking-[0.3em] text-gold"
          >
            {t.intro.eyebrow}
          </motion.p>
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: 64 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.1, delay: 0.25, ease: EASE }}
            className="h-px bg-gold/50 mt-4"
          />
        </div>

        <div className="lg:col-span-9">
          <motion.h2
            initial={{ opacity: 0, y: 32, filter: "blur(8px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1.1, ease: EASE }}
            className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.12] text-ink max-w-3xl"
          >
            {t.intro.heading[0]}
            <span className="italic text-gold">{t.intro.heading[1]}</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 28, filter: "blur(8px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1.1, delay: 0.15, ease: EASE }}
          >
            {t.brand.intro.map((paragraph, i) => (
              <p
                key={i}
                data-testid={i === 0 ? "main-description-text" : undefined}
                className={`leading-relaxed text-stone-700 max-w-3xl text-base sm:text-lg ${
                  i === 0 ? "mt-8" : "mt-5"
                }`}
              >
                {paragraph}
              </p>
            ))}
          </motion.div>

          <div
            id="przestrzenie"
            className="mt-14 grid sm:grid-cols-2 gap-3"
            data-testid="space-quick-nav"
          >
            {t.spaces.map((s, i) => (
              <motion.button
                key={s.id}
                data-testid="space-quick-link"
                onClick={() => scrollToHash(`#${s.id}`)}
                initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: 0.06 * i, ease: EASE }}
                className="group flex items-center justify-between border border-border-subtle rounded-full pl-6 pr-4 py-4 text-left hover:bg-charcoal hover:border-charcoal transition-colors duration-300"
              >
                <span className="flex items-baseline gap-4">
                  <span className="text-xs text-gold font-medium tracking-widest">
                    {s.num}
                  </span>
                  <span className="text-sm text-ink group-hover:text-cream transition-colors duration-300">
                    {s.title}
                  </span>
                </span>
                <ArrowUpRight
                  size={16}
                  className="text-gold opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-300"
                />
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Intro;
